import type { Difficulty, LifetimeStats } from "@eldrun/content";
import type { AlteracaoUsuario, AtivacaoFeita, AuditoriaItem, ConferenciaAtivacao, EmailItem, FiltroFeedback, FiltroLista, FiltroUsuarios, InscricaoAdmin, LancamentoAdmin, Pagina, PartidaAdmin, ResultadoLote, ResumoAdmin, SalaAoVivo, SituacaoLista, TipoEmail, UsuarioAdmin, UsuarioDetalhe } from "./admin-types.ts";

export interface Usuario {
  id: string;
  apelido: string;
  criadoEm: string;
}
export interface Sessao {
  token: string;
  usuario: Usuario;
}
export interface Me extends Usuario {
  email: string;
  /**
   * Tutorial na conta (F34): concluído ou desligado (`true`), religado de propósito (`false`) ou
   * nunca gravado (`null`).
   */
  tutorialConcluido: boolean | null;
  /** Papel da conta (F101): o site mostra o painel a moderador e admin. */
  papel: Papel;
  /** Pode jogar durante o pré-lançamento (F101). */
  acessoAntecipado: boolean;
}
export const ROLES = ["jogador", "moderador", "admin"] as const;
export type Papel = (typeof ROLES)[number];
/** O lançamento público (F101): a data da contagem, se o pré-lançamento está valendo e a hora do servidor. */
export interface Lancamento {
  lancamentoEm: string | null;
  preLancamento: boolean;
  agora: string;
}
/** Cadastro na lista de espera (F102). `website` é o campo escondido contra robôs (vazio para gente). */
export interface ListaEnvio {
  email: string;
  classe: string;
  idioma: "pt-BR" | "en-US";
  consentimento: boolean;
  website?: string;
}
/** A confirmação do e-mail da lista: a classe e a posição na fila. */
export interface ListaConfirmada {
  ok: true;
  classe: string;
  idioma: string;
  posicao: number | null;
}
export interface Conquista {
  id: string;
  nome: string;
  descricao: string;
  categoria: string;
}
export interface Perfil {
  apelido: string;
  criadoEm: string;
  estatisticas: LifetimeStats;
  conquistas: { id: string; nome: string; em: string }[];
}

/** Partida gravada pelo servidor de jogo (`GET /v1/matches/:id`), com as conquistas que ela deu. */
export interface PartidaGravada {
  partida: { id: string; modo: string; dificuldade: Difficulty; resultado: string; duracao: number; dia: number; criadoEm: string };
  jogadores: { usuarioId: string | null; apelido: string; vencedor: boolean }[];
  conquistas: { usuarioId: string; id: string; nome: string }[];
  /** A partida é gravada antes das conquistas: até `true`, a lista pode estar incompleta. */
  conquistasProntas: boolean;
}

export const RANKING_TYPES = ["fuga", "vitorias", "dia", "treta"] as const;
export type RankingTipo = (typeof RANKING_TYPES)[number];
export const PERIODS = ["geral", "semana", "mes"] as const;
export type Periodo = (typeof PERIODS)[number];
/** Faixas de grupo do ranking de fuga (F54: partidas de 1 a 4 jogadores). */
export const GROUPS = ["1", "2", "3-4"] as const;
export type Grupo = (typeof GROUPS)[number];

/** Recorte de um ranking (`periodo` geral, sem dificuldade e sem grupo são o padrão). */
export interface RankingFiltro {
  periodo: Periodo;
  dificuldade?: Difficulty;
  grupo?: Grupo;
}
/** Fuga mais rápida: uma partida, com os apelidos das contas que estavam nela. */
export interface FugaItem {
  posicao: number;
  partidaId: string;
  dificuldade: Difficulty;
  jogadores: number;
  duracao: number;
  dia: number;
  em: string;
  apelidos: string[];
}
/** Vitórias, maior dia ou Treta: um jogador e o valor (na Treta, vitórias e os abates do desempate). */
export interface JogadorItem {
  posicao: number;
  apelido: string;
  valor: number;
  abates?: number;
}
export type Ranking = { tipo: "fuga"; periodo: Periodo; itens: FugaItem[] } | { tipo: Exclude<RankingTipo, "fuga">; periodo: Periodo; itens: JogadorItem[] };

/** Categorias do feedback (F56), na ordem do formulário. */
export const FEEDBACK_CATEGORIES = ["bug", "ideia", "balanceamento", "outro"] as const;
export type FeedbackCategoria = (typeof FEEDBACK_CATEGORIES)[number];
/** Envio de feedback: `email` é opcional; `website` é o campo escondido contra robôs (vai vazio). */
export interface FeedbackEnvio {
  categoria: FeedbackCategoria;
  mensagem: string;
  email?: string;
  pagina?: string;
  website?: string;
}
/** Feedback gravado, como o admin lê (`GET /v1/feedback`). */
export interface FeedbackItem {
  id: string;
  categoria: FeedbackCategoria;
  mensagem: string;
  email: string | null;
  usuarioId: string | null;
  apelido: string | null;
  pagina: string | null;
  agente: string | null;
  criadoEm: string;
}
/** Uma página da leitura; `proximo` é o cursor da seguinte (ou `null` no fim). */
export interface FeedbackPagina {
  itens: FeedbackItem[];
  proximo: string | null;
}

/** Erro da API (`{ erro: { codigo, mensagem, campos? } }`) ou da rede (`status` 0). */
export class ApiError extends Error {
  readonly status: number;
  readonly codigo: string;
  readonly campos: Readonly<Record<string, string>>;

  constructor(status: number, codigo: string, mensagem: string, campos: Record<string, string> = {}) {
    super(mensagem);
    this.status = status;
    this.codigo = codigo;
    this.campos = campos;
  }
}

const OFFLINE = "Sem conexão com o servidor. Confira a internet e tente de novo.";
const SLOW = "O servidor demorou para responder. Tente de novo em instantes.";
/** Sem resposta nesse tempo, a chamada desiste (o botão não fica preso em "Entrando…"). */
export const TIMEOUT_MS = 15_000;

/** Lê o erro padrão da API; qualquer outra coisa vira um erro genérico com o status. */
function errorFrom(status: number, body: unknown): ApiError {
  const erro = (body as { erro?: { codigo?: unknown; mensagem?: unknown; campos?: unknown } } | null)?.erro;
  const campos: Record<string, string> = {};
  if (erro?.campos && typeof erro.campos === "object") {
    for (const [k, v] of Object.entries(erro.campos)) if (typeof v === "string") campos[k] = v;
  }
  const codigo = typeof erro?.codigo === "string" ? erro.codigo : "interno";
  const mensagem = typeof erro?.mensagem === "string" ? erro.mensagem : status >= 500 ? "O servidor teve um problema. Tente de novo em instantes." : `Falha inesperada (${status}).`;
  return new ApiError(status, codigo, mensagem, campos);
}

export type Fetcher = (input: string, init?: RequestInit) => Promise<Response>;

/** Cliente da API de contas e rankings (`/v1`). */
export function createApi(base: string, fetcher: Fetcher = (input, init) => fetch(input, init), timeoutMs = TIMEOUT_MS) {
  async function request<T>(path: string, init: { method?: string; body?: unknown; token?: string | undefined } = {}): Promise<T> {
    const headers: Record<string, string> = {};
    if (init.body !== undefined) headers["Content-Type"] = "application/json";
    if (init.token) headers["Authorization"] = `Bearer ${init.token}`;
    const abort = new AbortController();
    let late = false;
    const timer = setTimeout(() => {
      late = true;
      abort.abort();
    }, timeoutMs);
    let res: Response;
    let body: unknown = null;
    try {
      try {
        res = await fetcher(`${base}${path}`, {
          method: init.method ?? "GET",
          headers,
          signal: abort.signal,
          ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
        });
      } catch {
        throw new ApiError(0, "rede", late ? SLOW : OFFLINE);
      }
      try {
        body = await res.json();
      } catch {
        // Corpo vazio ou fora do JSON (proxy, página de erro): decide pelo status.
        if (late) throw new ApiError(0, "rede", SLOW);
      }
    } finally {
      clearTimeout(timer);
    }
    if (!res.ok) throw errorFrom(res.status, body);
    if (body === null) throw new ApiError(res.status, "resposta", "Resposta inesperada do servidor.");
    return body as T;
  }

  const query = (params: Record<string, string | undefined>) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v) q.set(k, v);
    const s = q.toString();
    return s ? `?${s}` : "";
  };

  return {
    register: (apelido: string, email: string, senha: string) => request<Sessao>("/v1/auth/register", { method: "POST", body: { apelido, email, senha } }),
    login: (login: string, senha: string) => request<Sessao>("/v1/auth/login", { method: "POST", body: { login, senha } }),
    me: (token: string) => request<Me>("/v1/me", { token }),
    /** Data do lançamento e pré-lançamento (F101): a contagem e o aviso de cadastro fechado. */
    launch: () => request<Lancamento>("/v1/launch"),
    /** Entra na lista de espera (F102): a resposta é sempre a mesma; o e-mail de confirmação sai se couber. */
    joinWaitlist: (envio: ListaEnvio) => request<{ ok: true }>("/v1/lista", { method: "POST", body: envio }),
    /** Confirma o e-mail da lista com o token do link (410 `link-invalido` se usado ou vencido). */
    confirmWaitlist: (token: string) => request<ListaConfirmada>("/v1/lista/confirmar", { method: "POST", body: { token } }),
    /** Sai da lista com o id e o token do link de saída. */
    leaveWaitlist: (id: string, token: string) => request<{ ok: true }>("/v1/lista/sair", { method: "POST", body: { id, token } }),
    setTutorial: (token: string, concluido: boolean) => request<{ tutorialConcluido: boolean }>("/v1/me/tutorial", { method: "PUT", body: { concluido }, token }),
    match: (id: string) => request<PartidaGravada>(`/v1/matches/${encodeURIComponent(id)}`),
    achievements: () => request<{ versao: number; conquistas: Conquista[] }>("/v1/achievements"),
    user: (apelido: string) => request<Perfil>(`/v1/users/${encodeURIComponent(apelido)}`),
    ranking: (tipo: RankingTipo, filtro: RankingFiltro) =>
      request<Ranking>(
        `/v1/rankings/${tipo}${query({
          periodo: filtro.periodo === "geral" ? undefined : filtro.periodo,
          // Na Treta a dificuldade não recorta; o grupo só existe na fuga.
          dificuldade: tipo === "treta" ? undefined : filtro.dificuldade,
          grupo: tipo === "fuga" ? filtro.grupo : undefined,
        })}`,
      ),
    /** Feedback do site (F56); com a sessão (opcional), vai com o apelido da conta. */
    sendFeedback: (envio: FeedbackEnvio, token?: string) => request<{ ok: true }>("/v1/feedback", { method: "POST", body: envio, token }),
    /**
     * Pede um link de senha nova (F121): a resposta é sempre a mesma (`202`), tenha o e-mail conta ou
     * não; o link chega por e-mail e leva à página `/ativar`. `idioma` vale se a conta não tem um.
     */
    requestPasswordReset: (email: string, idioma: "pt-BR" | "en-US", website = "") => request<{ ok: true }>("/v1/auth/recuperar", { method: "POST", body: { email, idioma, website } }),
    /** Página `/ativar` (F104): confere o link sem gastar e ativa (ou redefine a senha) com ele. */
    checkActivation: (token: string) => request<ConferenciaAtivacao>("/v1/ativacao/conferir", { method: "POST", body: { token } }),
    activate: (dados: { token: string; apelido?: string; senha: string }) => request<AtivacaoFeita>("/v1/ativacao", { method: "POST", body: dados }),
    /** O painel (F104): todas as rotas pedem a sessão de um moderador ou admin. */
    admin: {
      summary: (token: string) => request<ResumoAdmin>("/v1/admin/resumo", { token }),
      waitlist: (token: string, f: FiltroLista = {}) => request<Pagina<InscricaoAdmin>>(`/v1/admin/lista${query({ status: f.status, classe: f.classe, idioma: f.idioma, q: f.q, antes: f.antes, limite: f.limite === undefined ? undefined : String(f.limite) })}`, { token }),
      waitlistSummary: (token: string) => request<{ porStatus: Record<SituacaoLista, number>; porClasse: Record<string, number> }>("/v1/admin/lista/resumo", { token }),
      approve: (token: string, ids: string[]) => request<ResultadoLote>("/v1/admin/lista/aprovar", { method: "POST", body: { ids }, token }),
      reject: (token: string, ids: string[], notas?: string) => request<ResultadoLote>("/v1/admin/lista/recusar", { method: "POST", body: { ids, ...(notas ? { notas } : {}) }, token }),
      resend: (token: string, ids: string[]) => request<ResultadoLote>("/v1/admin/lista/reenviar", { method: "POST", body: { ids }, token }),
      deleteEntry: (token: string, id: string) => request<{ ok: true }>(`/v1/admin/lista/${encodeURIComponent(id)}`, { method: "DELETE", token }),
      users: (token: string, f: FiltroUsuarios = {}) => request<Pagina<UsuarioAdmin>>(`/v1/admin/usuarios${query({ q: f.q, papel: f.papel, status: f.status, antes: f.antes, limite: f.limite === undefined ? undefined : String(f.limite) })}`, { token }),
      user: (token: string, id: string) => request<UsuarioDetalhe>(`/v1/admin/usuarios/${encodeURIComponent(id)}`, { token }),
      updateUser: (token: string, id: string, mudanca: AlteracaoUsuario) => request<{ usuario: string; mudou: boolean }>(`/v1/admin/usuarios/${encodeURIComponent(id)}`, { method: "PATCH", body: mudanca, token }),
      resetUser: (token: string, id: string) => request<{ ok: true; tipo: "ativacao" | "redefinicao" }>(`/v1/admin/usuarios/${encodeURIComponent(id)}/redefinir`, { method: "POST", token }),
      matches: (token: string, f: { modo?: string | undefined; antes?: string | undefined; limite?: number | undefined } = {}) => request<Pagina<PartidaAdmin>>(`/v1/admin/partidas${query({ modo: f.modo, antes: f.antes, limite: f.limite === undefined ? undefined : String(f.limite) })}`, { token }),
      feedback: (token: string, f: FiltroFeedback = {}) => request<FeedbackPagina>(`/v1/admin/feedback${query({ categoria: f.categoria, antes: f.antes, limite: f.limite === undefined ? undefined : String(f.limite) })}`, { token }),
      audit: (token: string, f: { acao?: string | undefined; alvo?: string | undefined; antes?: string | undefined; limite?: number | undefined } = {}) => request<Pagina<AuditoriaItem>>(`/v1/admin/auditoria${query({ acao: f.acao, alvo: f.alvo, antes: f.antes, limite: f.limite === undefined ? undefined : String(f.limite) })}`, { token }),
      emails: (token: string, f: { status?: "enviado" | "falhou" | undefined; antes?: string | undefined; limite?: number | undefined } = {}) => request<Pagina<EmailItem>>(`/v1/admin/emails${query({ status: f.status, antes: f.antes, limite: f.limite === undefined ? undefined : String(f.limite) })}`, { token }),
      launch: (token: string) => request<LancamentoAdmin>("/v1/admin/launch", { token }),
      saveLaunch: (token: string, l: { lancamentoEm: string | null; preLancamento: boolean }) => request<LancamentoAdmin>("/v1/admin/launch", { method: "PUT", body: l, token }),
      testEmail: (token: string, e: { para: string; tipo: TipoEmail; idioma: "pt-BR" | "en-US" }) => request<{ ok: true; id: string }>("/v1/admin/email/teste", { method: "POST", body: e, token }),
      /** As salas abertas no servidor de partidas (F105); 503 `servidor-indisponivel` se ele não responde. */
      rooms: (token: string) => request<{ salas: SalaAoVivo[] }>("/v1/admin/salas", { token }),
      kick: (token: string, sala: string, sessao: string, motivo: string) => request<{ ok: true; apelido: string; usuarioId: string | null }>(`/v1/admin/salas/${encodeURIComponent(sala)}/expulsar`, { method: "POST", body: { sessao, motivo }, token }),
    },
  };
}

export type Api = ReturnType<typeof createApi>;
